import {
  departmentOf,
  type ImplementationTypology,
  implementationTypologies,
  type NetworkDashboardAvailableStore,
  type SavedStoreSelection,
  type SavedStoreSelectionInput,
  STORE_AGE_BRACKETS,
  type StoreAgeBracket,
} from '@gozoki/api-types'
import { normalizeSearchText } from '@gozoki/tools'
import { DateTime } from 'luxon'
import { getStoreAgeLabel } from '~/components/dashboards/store-age-label'
import {
  type NetworkDashboardDepartmentGroup,
  storeDepartment,
} from '~/components/dashboards/filters/group-stores-by-department'
import {
  STORE_AGE_BOUNDS_MONTHS,
  storeAgeBracketAt,
} from '~/components/dashboards/filters/store-age'
import { setsEqual } from '~/lib/sets'
import { storeTypologyLabel } from '~/components/dashboards/filters/store-typology'
import { matchesTextSearch } from '~/lib/text-search'
import { pluralize } from '~/lib/utils'

type Store = NetworkDashboardAvailableStore

export interface StoreSelection {
  zone: ReadonlySet<number>
  picked: ReadonlySet<number>
  ages: ReadonlySet<StoreAgeBracket>
  typologies: ReadonlySet<ImplementationTypology>
}

export type StoreSelectionIds = Omit<SavedStoreSelectionInput, 'name'> & {
  pickedStoreIds?: number[]
}

export type CheckState = 'checked' | 'unchecked' | 'indeterminate'

interface SelectionContext {
  stores: Store[]
  periodEnd: string
}

interface SelectionParams extends StoreSelection, SelectionContext {}

export interface StoreSelectorButtonLabel {
  title: string
  empty?: true
}

export type CriterionValue =
  | { kind: 'age'; value: string; brackets: StoreAgeBracket[]; label: string }
  | { kind: 'typology'; value: ImplementationTypology; label: string }

export interface SelectionToken {
  key: string
  label: string
  count?: number
  stores: Store[]
  criterion?: CriterionValue
  group?: NetworkDashboardDepartmentGroup
}

export type Criterion = CriterionValue & { count: number }

export const CRITERION_GROUP_LABELS: Record<Criterion['kind'], string> = {
  age: 'Ancienneté',
  typology: 'Typologie',
}

export interface SelectorSearchResults {
  cities: { city: string; stores: Store[]; selectedCount: number }[]
  departments: { group: NetworkDashboardDepartmentGroup; selectedCount: number }[]
  criteria: Criterion[]
  stores: Store[]
}

export const ageBracketLabel = (bracket: StoreAgeBracket): string =>
  bracket === 'more_than_12_months' ? '+ 12 mois' : getStoreAgeLabel(bracket)

const ageCriterion = (brackets: StoreAgeBracket[], label: string): CriterionValue => ({
  kind: 'age',
  value: brackets.join('+'),
  brackets,
  label,
})

const bracketCriterion = (bracket: StoreAgeBracket): CriterionValue =>
  ageCriterion([bracket], ageBracketLabel(bracket))

function ageRangeCriterion(direction: 'more' | 'less', months: number): CriterionValue | null {
  const bound = (STORE_AGE_BOUNDS_MONTHS as readonly number[]).indexOf(months)
  if (bound === -1) return null
  const brackets =
    direction === 'more'
      ? STORE_AGE_BRACKETS.slice(bound + 1)
      : STORE_AGE_BRACKETS.slice(0, bound + 1)
  if (brackets.length < 2) return null
  return ageCriterion(brackets, direction === 'more' ? `+ de ${months} mois` : `< ${months} mois`)
}

const AGE_RANGES = STORE_AGE_BOUNDS_MONTHS.flatMap((months) => [
  ageRangeCriterion('more', months),
  ageRangeCriterion('less', months),
]).filter((range): range is CriterionValue & { kind: 'age' } => range !== null)

function ageCriteria(ages: ReadonlySet<StoreAgeBracket>): CriterionValue[] {
  const selected = STORE_AGE_BRACKETS.filter((bracket) => ages.has(bracket))
  const range = AGE_RANGES.find(
    ({ brackets }) =>
      brackets.length === selected.length && brackets.every((bracket) => ages.has(bracket)),
  )
  return range ? [range] : selected.map(bracketCriterion)
}

const AGE_RANGE_QUERY = /^(\+|plus|>|-|moins|<)\s*(?:de\s*)?(\d+)/

function queriedAgeRange(query: string): CriterionValue | null {
  const match = AGE_RANGE_QUERY.exec(normalizeSearchText(query).trim())
  if (!match) return null
  const more = ['+', 'plus', '>'].includes(match[1])
  const months = Number(match[2])
  const bound = (STORE_AGE_BOUNDS_MONTHS as readonly number[]).indexOf(months)
  if (more && bound === STORE_AGE_BOUNDS_MONTHS.length - 1) {
    return bracketCriterion('more_than_12_months')
  }
  if (!more && bound === 0) return bracketCriterion('less_than_3_months')
  return ageRangeCriterion(more ? 'more' : 'less', months)
}

const NO_STORES: ReadonlySet<number> = new Set()

const sortedIds = (ids: Iterable<number>): number[] => [...ids].sort((a, b) => a - b)

export const EMPTY_SELECTION: StoreSelection = {
  zone: NO_STORES,
  picked: NO_STORES,
  ages: new Set(),
  typologies: new Set(),
}

export const allStoresSelection = (stores: Store[]): StoreSelection => ({
  ...EMPTY_SELECTION,
  zone: new Set(stores.map((store) => store.id)),
})

export const hasCriteria = ({ ages, typologies }: Pick<StoreSelection, 'ages' | 'typologies'>) =>
  ages.size + typologies.size > 0

export const hasChoice = (selection: StoreSelection): boolean =>
  selection.zone.size > 0 || selection.picked.size > 0 || hasCriteria(selection)

const coversAll = (zone: ReadonlySet<number>, stores: Store[]): boolean =>
  stores.every((store) => zone.has(store.id))

const extraPicked = ({ zone, picked }: StoreSelection): number[] =>
  [...picked].filter((id) => !zone.has(id))

export const isAllStoresSelection = (selection: StoreSelection, stores: Store[]): boolean =>
  !hasCriteria(selection) && selection.picked.size === 0 && coversAll(selection.zone, stores)

export const choiceBase = (selection: StoreSelection, fresh: boolean): StoreSelection =>
  fresh ? EMPTY_SELECTION : selection

type CriteriaFilter = Pick<SelectionParams, 'ages' | 'typologies' | 'periodEnd'>

function matchesCriteria({
  ages,
  typologies,
  periodEnd,
}: CriteriaFilter): (store: Store) => boolean {
  const bracketOf = storeAgeBracketAt(periodEnd)
  return (store) =>
    (ages.size === 0 || ages.has(bracketOf(store))) &&
    (typologies.size === 0 || (store.typology != null && typologies.has(store.typology)))
}

function inZone(selection: StoreSelection): (store: Store) => boolean {
  if (selection.zone.size > 0) return (store) => selection.zone.has(store.id)
  return () => hasCriteria(selection)
}

export function resolveIncludedStoreIds({
  stores,
  periodEnd,
  ...selection
}: SelectionParams): number[] {
  const matches = matchesCriteria({ ...selection, periodEnd })
  const zone = inZone(selection)
  return stores
    .filter((store) => selection.picked.has(store.id) || (zone(store) && matches(store)))
    .map((store) => store.id)
}

export const selectedCount = (stores: Store[], selected: ReadonlySet<number>): number =>
  stores.filter((store) => selected.has(store.id)).length

export function checkState(stores: Store[], selected: ReadonlySet<number>): CheckState {
  const count = selectedCount(stores, selected)
  if (count === 0) return 'unchecked'
  return count === stores.length ? 'checked' : 'indeterminate'
}

const without = <T>(set: ReadonlySet<T>, values: T[]): Set<T> => {
  const next = new Set(set)
  for (const value of values) next.delete(value)
  return next
}

export const includedSet = (selection: StoreSelection, context: SelectionContext): Set<number> =>
  new Set(resolveIncludedStoreIds({ ...context, ...selection }))

const withIds = <T>(set: ReadonlySet<T>, values: T[]): Set<T> => new Set([...set, ...values])

function removeFromResult(
  selection: StoreSelection,
  ids: number[],
  context: SelectionContext,
): StoreSelection {
  const remaining = resolveIncludedStoreIds({ ...context, ...selection }).filter(
    (id) => !ids.includes(id),
  )
  const narrowed = {
    ...selection,
    zone: without(selection.zone, ids),
    picked: without(selection.picked, ids),
  }
  if (setsEqual(includedSet(narrowed, context), new Set(remaining))) return narrowed
  return { ...EMPTY_SELECTION, picked: new Set(remaining) }
}

export const groupChosen = (selection: StoreSelection, group: Store[]): boolean =>
  group.every((store) => selection.zone.has(store.id) || selection.picked.has(store.id))

export function toggleGroupSelection(
  selection: StoreSelection,
  group: Store[],
  context: SelectionContext,
): StoreSelection {
  const ids = group.map((store) => store.id)
  if (groupChosen(selection, group)) return removeFromResult(selection, ids, context)
  return { ...selection, zone: withIds(selection.zone, ids) }
}

export function toggleStoreSelection(
  selection: StoreSelection,
  store: Store,
  context: SelectionContext,
): StoreSelection {
  if (includedSet(selection, context).has(store.id)) {
    return removeFromResult(selection, [store.id], context)
  }
  return { ...selection, picked: withIds(selection.picked, [store.id]) }
}

export function removeToken(selection: StoreSelection, token: SelectionToken): StoreSelection {
  if (token.criterion) return toggleCriterion(selection, token.criterion)
  const ids = token.stores.map((store) => store.id)
  return {
    ...selection,
    zone: without(selection.zone, ids),
    picked: without(selection.picked, ids),
  }
}

export const toggleValue = <T>(set: ReadonlySet<T>, value: T): Set<T> => {
  const next = new Set(set)
  if (!next.delete(value)) next.add(value)
  return next
}

export const completeGroups = (
  groups: NetworkDashboardDepartmentGroup[],
  selected: ReadonlySet<number>,
): NetworkDashboardDepartmentGroup[] =>
  groups.filter((group) => checkState(group.stores, selected) === 'checked')

const storeToken = (prefix: string, store: Store): SelectionToken => ({
  key: `${prefix}:${store.id}`,
  label: store.label,
  stores: [store],
})

function storesByCity(stores: Store[]): Map<string, { city: string; stores: Store[] }> {
  const byCity = new Map<string, { city: string; stores: Store[] }>()
  for (const store of stores) {
    if (!store.city) continue
    const key = normalizeSearchText(store.city)
    const entry = byCity.get(key) ?? { city: store.city, stores: [] }
    if (entry.city === entry.city.toUpperCase()) entry.city = store.city
    entry.stores.push(store)
    byCity.set(key, entry)
  }
  return byCity
}

function zoneTokens(
  groups: NetworkDashboardDepartmentGroup[],
  zone: ReadonlySet<number>,
): SelectionToken[] {
  const complete = completeGroups(groups, zone)
  const completeCodes = new Set(complete.map((group) => group.code))
  const rest = groups
    .filter((group) => !completeCodes.has(group.code))
    .flatMap((group) => group.stores.filter((store) => zone.has(store.id)))
  const network = storesByCity(groups.flatMap((group) => group.stores))
  const tokens: SelectionToken[] = complete.map((group) => ({
    key: `department:${group.code}`,
    label: group.label,
    count: group.stores.length,
    stores: group.stores,
    group,
  }))
  const singles: SelectionToken[] = []
  for (const [key, { stores: list }] of storesByCity(rest)) {
    const city = network.get(key)!
    if (list.length > 1 && list.length === city.stores.length) {
      tokens.push({ key: `city:${key}`, label: city.city, count: list.length, stores: list })
    } else {
      singles.push(...list.map((store) => storeToken('store', store)))
    }
  }
  singles.push(...rest.filter((store) => !store.city).map((store) => storeToken('store', store)))
  return [...tokens, ...singles]
}

const criterionToken = (criterion: CriterionValue): SelectionToken => ({
  key: `${criterion.kind}:${criterion.value}`,
  label: criterion.label,
  stores: [],
  criterion,
})

export function selectionTokens(
  selection: StoreSelection,
  { stores, groups }: { stores: Store[]; groups: NetworkDashboardDepartmentGroup[] },
): SelectionToken[] {
  const extra = new Set(extraPicked(selection))
  return [
    ...(coversAll(selection.zone, stores) ? [] : zoneTokens(groups, selection.zone)),
    ...[...ageCriteria(selection.ages), ...typologyCriteria(selection.typologies)].map(
      criterionToken,
    ),
    ...stores.filter((store) => extra.has(store.id)).map((store) => storeToken('picked', store)),
  ]
}

export function coveringDepartments(
  groups: NetworkDashboardDepartmentGroup[],
  ids: ReadonlySet<number>,
): NetworkDashboardDepartmentGroup[] | null {
  const complete = completeGroups(groups, ids)
  const covered = complete.reduce((sum, group) => sum + group.stores.length, 0)
  return complete.length > 0 && covered === ids.size ? complete : null
}

export function visibleTokenCount(
  widths: number[],
  available: number,
  moreWidth: number,
  gap: number,
): number {
  const lineWidth = (count: number) =>
    widths.slice(0, count).reduce((sum, width) => sum + width, 0) + gap * Math.max(count - 1, 0)
  if (lineWidth(widths.length) <= available) return widths.length
  let count = widths.length - 1
  while (count > 0 && lineWidth(count) + gap + moreWidth > available) count -= 1
  return count
}

export const touchedDepartmentCount = (
  groups: NetworkDashboardDepartmentGroup[],
  ids: ReadonlySet<number>,
): number => groups.filter((group) => group.stores.some((store) => ids.has(store.id))).length

export function storeSelectorButtonLabel({
  stores,
  included,
}: {
  stores: Store[]
  included: ReadonlySet<number>
}): StoreSelectorButtonLabel {
  if (included.size === 0) return { title: 'Aucune boutique', empty: true }
  if (included.size === stores.length) return { title: 'Toutes les boutiques' }
  return { title: pluralize(included.size, 'boutique') }
}

export const departmentName = (group: NetworkDashboardDepartmentGroup): string =>
  (group.departmentCode !== null ? departmentOf(group.departmentCode)?.name : undefined) ??
  group.label

const MAX_NAMED_DEPARTMENTS = 2

function zoneHeadline(
  selection: StoreSelection,
  { stores, groups }: { stores: Store[]; groups: NetworkDashboardDepartmentGroup[] },
): string | null {
  const extra = extraPicked(selection).length > 0
  if (selection.picked.size > 0 && hasCriteria(selection)) return 'Sélection'
  if (!extra && (selection.zone.size === 0 || coversAll(selection.zone, stores))) return null
  if (extra && selection.zone.size > 0) return 'Sélection'
  const tokens = zoneTokens(groups, withIds(selection.zone, [...selection.picked]))
  const departments = tokens.flatMap((token) => (token.group ? [token.group] : []))
  if (tokens.length === 1) {
    return departments.length === 1 ? departmentName(departments[0]) : tokens[0].label
  }
  if (departments.length !== tokens.length) return 'Sélection'
  return departments.length <= MAX_NAMED_DEPARTMENTS
    ? departments.map(departmentName).join(', ')
    : pluralize(departments.length, 'département')
}

const labelsOf = (criteria: CriterionValue[]): string =>
  criteria.map((criterion) => criterion.label).join(', ')

export function selectionHeadline(
  selection: StoreSelection,
  context: { stores: Store[]; groups: NetworkDashboardDepartmentGroup[] },
): string[] {
  return [
    zoneHeadline(selection, context),
    labelsOf(ageCriteria(selection.ages)),
    labelsOf(typologyCriteria(selection.typologies)),
  ].filter((part): part is string => Boolean(part))
}

const presentTypologies = (stores: Store[]): ImplementationTypology[] =>
  implementationTypologies.filter((typology) => stores.some((store) => store.typology === typology))

const criterionMatcher = (criterion: CriterionValue, periodEnd: string) =>
  matchesCriteria({
    ages: new Set(criterion.kind === 'age' ? criterion.brackets : []),
    typologies: new Set(criterion.kind === 'typology' ? [criterion.value] : []),
    periodEnd,
  })

const typologyCriteria = (typologies: ReadonlySet<ImplementationTypology>): CriterionValue[] =>
  implementationTypologies
    .filter((typology) => typologies.has(typology))
    .map((value) => ({ kind: 'typology', value, label: storeTypologyLabel(value) }))

const criteriaCatalogue = (stores: Store[]): CriterionValue[] => [
  ...STORE_AGE_BRACKETS.map(bracketCriterion),
  ...typologyCriteria(new Set(presentTypologies(stores))),
]

function countedCriteria(
  values: CriterionValue[],
  selection: StoreSelection,
  context: SelectionContext,
): Criterion[] {
  const included = includedSet(selection, context)
  const result = context.stores.filter((store) => included.has(store.id))
  return values.map((value) => ({
    ...value,
    count: criterionActive(value, selection)
      ? result.filter(criterionMatcher(value, context.periodEnd)).length
      : resolveIncludedStoreIds({ ...context, ...toggleCriterion(selection, value) }).length,
  }))
}

export const criteriaOptions = (
  stores: Store[],
  periodEnd: string,
  selection: StoreSelection = EMPTY_SELECTION,
): Criterion[] => countedCriteria(criteriaCatalogue(stores), selection, { stores, periodEnd })

export const criterionActive = (criterion: CriterionValue, selection: StoreSelection): boolean =>
  criterion.kind === 'age'
    ? criterion.brackets.every((bracket) => selection.ages.has(bracket))
    : selection.typologies.has(criterion.value)

export const toggleCriterion = (
  selection: StoreSelection,
  criterion: CriterionValue,
): StoreSelection =>
  criterion.kind === 'age'
    ? {
        ...selection,
        ages: criterionActive(criterion, selection)
          ? without(selection.ages, criterion.brackets)
          : withIds(selection.ages, criterion.brackets),
      }
    : { ...selection, typologies: toggleValue(selection.typologies, criterion.value) }

export function storeMeta(store: Store): string {
  return [storeDepartment(store).label, store.city].filter(Boolean).join(' - ')
}

export function searchSelector({
  groups,
  query,
  selection,
  periodEnd,
}: {
  groups: NetworkDashboardDepartmentGroup[]
  query: string
  selection: StoreSelection
  periodEnd: string
}): SelectorSearchResults {
  const bracketOf = storeAgeBracketAt(periodEnd)
  const stores = groups.flatMap((group) => group.stores)
  const included = includedSet(selection, { stores, periodEnd })

  const byCity = storesByCity(
    stores.filter((store) => store.city && matchesTextSearch([store.city], query)),
  )
  const cities = [...byCity.values()]
    .sort((a, b) => a.city.localeCompare(b.city, 'fr'))
    .map(({ city, stores: cityStores }) => ({
      city,
      stores: cityStores,
      selectedCount: selectedCount(cityStores, included),
    }))

  const departments = groups
    .filter((group) => matchesTextSearch([group.label, group.shortLabel], query))
    .map((group) => ({ group, selectedCount: selectedCount(group.stores, included) }))

  const range = queriedAgeRange(query)
  const criteria = countedCriteria(
    [
      ...(range ? [range] : []),
      ...criteriaCatalogue(stores).filter(
        (criterion) =>
          criterion.value !== range?.value && matchesTextSearch([criterion.label], query),
      ),
    ],
    selection,
    { stores, periodEnd },
  )

  const matchingStores = stores.filter((store) =>
    matchesTextSearch(
      [
        store.label,
        store.fullLabel,
        store.shortLabel,
        store.city,
        storeDepartment(store).label,
        store.typology == null ? null : storeTypologyLabel(store.typology),
        ageBracketLabel(bracketOf(store)),
      ],
      query,
    ),
  )

  return { cities, departments, criteria, stores: matchingStores }
}

export function browseSummary(
  groups: NetworkDashboardDepartmentGroup[],
  included: ReadonlySet<number>,
): string {
  if (included.size === 0) return 'Aucune boutique sélectionnée'
  return `${pluralize(included.size, 'boutique')} et ${pluralize(
    touchedDepartmentCount(groups, included),
    'département',
  )} sélectionnés`
}

export const criteriaLabel = ({
  ages,
  typologies,
}: Pick<StoreSelection, 'ages' | 'typologies'>): string =>
  labelsOf([...ageCriteria(ages), ...typologyCriteria(typologies)])

export function recentSelectionRow({
  stores,
  groups,
  periodEnd,
  selection,
}: {
  stores: Store[]
  groups: NetworkDashboardDepartmentGroup[]
  periodEnd: string
  selection: StoreSelection
}): { title: string; meta: string } {
  const included = new Set(resolveIncludedStoreIds({ stores, periodEnd, ...selection }))
  const criteria = criteriaLabel(selection)
  if (!criteria) {
    const covering = coveringDepartments(groups, included)
    let title: string | null = null
    if (included.size === stores.length) title = 'Toutes les boutiques'
    else if (covering) title = covering.map((group) => group.label).join(', ')
    else if (included.size === 1) title = stores.find((store) => included.has(store.id))!.label
    if (title !== null) return { title, meta: pluralize(included.size, 'boutique') }
    return {
      title: pluralize(included.size, 'boutique'),
      meta: pluralize(touchedDepartmentCount(groups, included), 'département'),
    }
  }
  const { zone } = selection
  const covering = coveringDepartments(groups, zone)
  let base = pluralize(zone.size, 'boutique')
  if (zone.size === 0 || coversAll(zone, stores)) base = 'Toutes les boutiques'
  else if (covering) base = covering.map((group) => group.label).join(', ')
  else if (zone.size === 1) base = stores.find((store) => zone.has(store.id))!.label
  const added = extraPicked(selection).length
  const addedLabel = added > 0 ? ` + ${pluralize(added, 'boutique')}` : ''
  return { title: `${criteria} · ${base}${addedLabel}`, meta: pluralize(included.size, 'boutique') }
}

export function idsFromSelection(
  selection: StoreSelection,
  stores: Store[],
): Required<StoreSelectionIds> {
  return {
    storeIds: isAllStoresSelection(selection, stores) ? [] : sortedIds(selection.zone),
    pickedStoreIds: sortedIds(selection.picked),
    ages: STORE_AGE_BRACKETS.filter((bracket) => selection.ages.has(bracket)),
    typologies: implementationTypologies.filter((typology) => selection.typologies.has(typology)),
  }
}

export function selectionFromIds(ids: StoreSelectionIds, stores: Store[]): StoreSelection {
  const known = new Set(stores.map((store) => store.id))
  const picked = new Set((ids.pickedStoreIds ?? []).filter((id) => known.has(id)))
  const zone = ids.storeIds.filter((id) => known.has(id))
  if (ids.storeIds.length > 0 && zone.length === 0) return { ...EMPTY_SELECTION, picked }
  const selection = {
    zone: new Set(zone),
    picked,
    ages: new Set(ids.ages),
    typologies: new Set(
      presentTypologies(stores).filter((typology) => ids.typologies.includes(typology)),
    ),
  }
  if (hasChoice(selection)) return selection
  return (ids.pickedStoreIds ?? []).length > 0 ? EMPTY_SELECTION : allStoresSelection(stores)
}

export const sameSelection = (a: StoreSelection, b: StoreSelection): boolean =>
  setsEqual(a.zone, b.zone) &&
  setsEqual(a.picked, b.picked) &&
  setsEqual(a.ages, b.ages) &&
  setsEqual(a.typologies, b.typologies)

export function applyDecision({
  pending,
  applied,
  stores,
  periodEnd,
}: {
  pending: StoreSelection
  applied: StoreSelection
  stores: Store[]
  periodEnd: string
}): { changed: boolean; recent: Required<StoreSelectionIds> | null } | null {
  if (resolveIncludedStoreIds({ stores, periodEnd, ...pending }).length === 0) return null
  const recent = isAllStoresSelection(pending, stores) ? null : idsFromSelection(pending, stores)
  return { changed: !sameSelection(pending, applied), recent }
}

export function savedSelectionInput(
  name: string,
  selection: StoreSelection,
  context: SelectionContext,
): SavedStoreSelectionInput {
  const { pickedStoreIds, ...ids } = idsFromSelection(selection, context.stores)
  if (pickedStoreIds.length === 0) return { name, ...ids }
  const included = resolveIncludedStoreIds({ ...context, ...selection })
  const everyStore = included.length === context.stores.length
  return { name, storeIds: everyStore ? [] : included, ages: [], typologies: [] }
}

export const replacedSelection = (
  name: string,
  selections: SavedStoreSelection[],
): SavedStoreSelection | undefined => selections.find((selection) => selection.name === name.trim())

const WEEKDAYS = ['', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim']

export function recentAge(at: string, now: DateTime): string {
  const date = DateTime.fromISO(at)
  const minutes = Math.floor(now.diff(date, 'minutes').minutes)
  const days = Math.round(now.startOf('day').diff(date.startOf('day'), 'days').days)
  if (minutes < 60) return `${Math.max(minutes, 1)} min`
  if (days === 0) return `${Math.floor(minutes / 60)}h`
  if (days === 1) return 'hier'
  if (days < 7) return WEEKDAYS[date.weekday]
  return date.setLocale('fr').toFormat('d LLL')
}
